import { mdiBookmarkOutline, mdiTranslate } from '@mdi/js'
import Icon from '@mdi/react'
import { useNavigate } from '@tanstack/react-router'
import { PrimitiveAtom, useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'

import FormatQuote from '@/components/assets/FormatQuote'
import InsertDriveFile from '@/components/assets/InsertDriveFile'
import LocalLibrary from '@/components/assets/LocalLibrary'
import { Box } from '@/components/ui/box'
import { Stack } from '@/components/ui/stack'
import useColorModeValue from '@/hooks/useColorModeValue'
import useCustomMediaQuery from '@/hooks/useCustomMediaQuery'
import { store } from '@/store'
import {
    chatSettingsAtom,
    exampleTypeAtom,
    showSettingsSwitchMessage,
    snippetSourcesAtom,
    userMembershipAtom,
} from '@/store/gigiChat/ChatAtom'
import { endPromptConversation } from '@/store/gigiChat/Visuals'
import { IConversationSettings, ISnippetSourceDetails, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'
import { TChatGPTModel } from '@/types/gigiChat/Generic'
import { deepClone } from '@/utils'
import { getSnippetSourcesForBundle } from '@/utils/systemGroups'

import MobileSettingsOptions from '../../../mobileSettingsOptions'
import SettingsTag from './Tag'

const SelectSettingsOption = ({ processingStrategy }: { processingStrategy?: TProcessingStrategy }) => {
    const { t } = useTranslation()
    const [snippetSources] = useAtom<ISnippetSourceDetails>(snippetSourcesAtom)
    const [userGroups] = useAtom<Array<string>>(userMembershipAtom)
    const [chatSettings, setChatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [isMobile] = useCustomMediaQuery()
    // TO Do: replace hardcoded colors values once we determine how we can pass chakra colors to svg icons component
    const iconsActiveColor = useColorModeValue('white', '#2b2b2a')
    const iconsColor = useColorModeValue('#2b2b2a', 'white')
    const navigate = useNavigate()
    const isQa = processingStrategy === 'QA'
    const isWriting = processingStrategy === 'Writing'
    const isDocAnalysis = processingStrategy === 'DocAnalysis'
    const isTranslate = processingStrategy === 'Translation'
    const isPrompt = chatSettings?.processingStrategyWriting.mode === 'promptLibrary'
    const promptLabel = chatSettings?.processingModePromptLibrary?.label || ''

    const svgProps = {
        width: '16px',
        height: '16px',
    }

    const handleSettingsChange = (processingStrategy: TProcessingStrategy, key: string, checkValue: string | number) => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        const { processingStrategy: prevProcessingStrategy } = localSettings
        localSettings.processingStrategy = processingStrategy

        if (processingStrategy === 'Writing') {
            if (key === 'model') {
                localSettings.processingStrategyWriting.model = checkValue as TChatGPTModel
            }

            if (key === 'temperature') {
                localSettings.processingStrategyWriting.temperature = +checkValue
            }
        }

        if (processingStrategy === 'QA') {
            store.set(exampleTypeAtom, 'QA')
            const snippets = getSnippetSourcesForBundle(key, userGroups, snippetSources)
            localSettings.processingStrategyQa.snippetSources = snippets
        }

        if (processingStrategy !== chatSettings?.processingStrategy) {
            showSettingsSwitchMessage(processingStrategy)
        }

        setChatSettings((prev) => ({
            ...prev,
            ...localSettings,
        }))
        store.set(chatSettingsAtom, localSettings)

        if (prevProcessingStrategy === 'DocAnalysis' || prevProcessingStrategy === 'Translation') {
            void navigate({ to: '/chat' })
        }
    }

    const disabled = !processingStrategy

    return (
        <>
            <Stack direction="row" gap="8px" align="center" flexWrap="wrap" justifyContent="left" data-testid="settings-tags">
                {isPrompt ? (
                    <SettingsTag
                        disabled={disabled}
                        dataTestid="settings-tag-prompt"
                        isActive={true}
                        onClick={() => null}
                        label={{ mobile: promptLabel, desktop: promptLabel }}
                        icon={<Icon path={mdiBookmarkOutline} style={svgProps} />}
                    />
                ) : (
                    <>
                        <SettingsTag
                            disabled={disabled}
                            dataTestid="settings-tag-qa"
                            isActive={isQa}
                            onClick={() => handleSettingsChange('QA', snippetSources.defaultBundleName, snippetSources.defaultBundleName)}
                            label={{
                                mobile: t('chat.settings.chips.mobile.searchGSKKnowledge'),
                                desktop: t('chat.settings.chips.searchGSKKnowledge'),
                            }}
                            icon={<InsertDriveFile style={svgProps} color={isQa ? iconsActiveColor : iconsColor} />}
                        />
                        <SettingsTag
                            disabled={disabled}
                            dataTestid="settings-tag-writing"
                            isActive={isWriting}
                            onClick={() => handleSettingsChange('Writing', 'model', 'gpt-4')}
                            label={{
                                mobile: t('chat.settings.chips.mobile.helpWithWriting'),
                                desktop: t('chat.settings.chips.helpWithWriting'),
                            }}
                            icon={<FormatQuote style={svgProps} color={isWriting ? iconsActiveColor : iconsColor} />}
                        />
                    </>
                )}

                <SettingsTag
                    disabled={disabled}
                    dataTestid="settings-tag-translate"
                    isActive={isTranslate}
                    onClick={() => {
                        endPromptConversation()
                        void navigate({
                            to: '/translate',
                        })
                    }}
                    label={{
                        mobile: t('chat.settings.chips.mobile.translate'),
                        desktop: t('chat.settings.chips.translate'),
                    }}
                    icon={<Icon path={mdiTranslate} style={svgProps} color={isTranslate ? iconsActiveColor : iconsColor} />}
                />

                <SettingsTag
                    disabled={disabled}
                    dataTestid="settings-tag-documents"
                    isActive={isDocAnalysis}
                    onClick={() => {
                        endPromptConversation()
                        void navigate({
                            to: '/documents',
                        })
                    }}
                    label={{
                        mobile: t('chat.settings.chips.mobile.analyseDocuments'),
                        desktop: t('chat.settings.chips.analyseDocuments'),
                    }}
                    icon={<LocalLibrary style={svgProps} color={isDocAnalysis ? iconsActiveColor : iconsColor} />}
                />
            </Stack>
            {isMobile && (
                <Box mt="8px">
                    <MobileSettingsOptions />
                </Box>
            )}
        </>
    )
}

export default SelectSettingsOption
